/**
 * Event Tab Block V2 Component
 * Handles tab switching interaction for event tabs
 */
// Storage key for persisting tab parameters across page refreshes
const STORAGE_KEY = 'eventTabParams';
/**
 * EventTabs component
 * Manages tab navigation and content visibility
 */
export const EventTabs = (element) => {
    // Find all tab buttons, content panels, and titles
    const tabButtons = element.querySelectorAll('[data-action="event-item"]');
    const tabContents = element.querySelectorAll('[data-action="event-tab-content"]');
    const tabTitles = element.querySelectorAll('[data-action="event-tab-title"]');
    if (!tabButtons.length || !tabContents.length) {
        console.warn("EventTabs: Required elements not found");
        return;
    }
    // Store parameters for each tab independently
    const tabParams = new Map();
    // Load saved tab parameters from sessionStorage
    const loadTabParamsFromStorage = () => {
        try {
            const stored = sessionStorage.getItem(STORAGE_KEY);
            if (stored) {
                const parsed = JSON.parse(stored);
                Object.entries(parsed).forEach(([key, value]) => {
                    tabParams.set(parseInt(key, 10), value);
                });
            }
        }
        catch (error) {
            console.warn("EventTabs: Failed to load tab params from storage", error);
        }
    };
    // Save tab parameters to sessionStorage
    const saveTabParamsToStorage = () => {
        try {
            const paramsObject = {};
            tabParams.forEach((value, key) => {
                paramsObject[key] = value;
            });
            sessionStorage.setItem(STORAGE_KEY, JSON.stringify(paramsObject));
        }
        catch (error) {
            console.warn("EventTabs: Failed to save tab params to storage", error);
        }
    };
    // Load saved parameters on initialization
    loadTabParamsFromStorage();
    // Track the current active tab index
    let currentActiveTabIndex = 0;
    // Initialize: Find which tab is currently active and store initial params
    const initialParams = window.location.search;
    const initialHash = window.location.hash;
    if (initialHash) {
        const hashWithoutSymbol = initialHash.substring(1);
        const activeButton = Array.from(tabButtons).find((button) => button.id === hashWithoutSymbol);
        if (activeButton) {
            currentActiveTabIndex = parseInt(activeButton.getAttribute("data-tab-index") || "0", 10);
        }
    }
    else {
        // Find the currently active tab (has active classes)
        const activeButton = Array.from(tabButtons).find((button) => button.classList.contains("text-green-700"));
        if (activeButton) {
            currentActiveTabIndex = parseInt(activeButton.getAttribute("data-tab-index") || "0", 10);
        }
    }
    // Store initial params for the active tab (merge with any existing stored params)
    if (initialParams) {
        tabParams.set(currentActiveTabIndex, initialParams);
        saveTabParamsToStorage();
    }
    /**
     * Save current parameters for a specific tab
     */
    const saveTabParams = (tabIndex) => {
        const currentParams = window.location.search;
        if (currentParams) {
            tabParams.set(tabIndex, currentParams);
        }
        else {
            // If no params, remove the entry to avoid storing empty strings
            tabParams.delete(tabIndex);
        }
        // Persist to sessionStorage
        saveTabParamsToStorage();
    };
    /**
     * Update URL with tab-specific parameters when switching tabs
     */
    const updateUrlForTab = (targetIndex) => {
        // Save current tab's parameters before switching
        saveTabParams(currentActiveTabIndex);
        // Get the target tab button to check if it has an id for hash
        const targetButton = Array.from(tabButtons).find((btn) => parseInt(btn.getAttribute("data-tab-index") || "0", 10) === targetIndex);
        // Get stored parameters for the target tab, or use empty string
        const storedParams = tabParams.get(targetIndex) || '';
        // Build new URL with tab-specific parameters
        const pathname = window.location.pathname;
        const hash = targetButton?.id ? `#${targetButton.id}` : '';
        const newUrl = `${pathname}${storedParams}${hash}`;
        // Update URL without page reload
        window.history.pushState({ tabIndex: targetIndex }, '', newUrl);
        // Update current active tab index
        currentActiveTabIndex = targetIndex;
    };
    /**
     * Activate a specific tab by index
     */
    const activateTab = (targetIndex) => {
        // Update all tab buttons
        tabButtons.forEach((button) => {
            const tabIndex = parseInt(button.getAttribute("data-tab-index") || "0", 10);
            const isActive = tabIndex === targetIndex;
            // Update button styles
            if (isActive) {
                button.classList.remove("text-gray-500", "hover:text-gray-700");
                button.classList.add("text-green-700");
                // Add active underline indicator
                let underline = button.querySelector(".absolute.bottom-0");
                if (!underline) {
                    underline = document.createElement("div");
                    underline.className = "absolute bottom-0 left-0 right-0 h-0.5 bg-green-700";
                    button.appendChild(underline);
                }
            }
            else {
                button.classList.remove("text-green-700");
                button.classList.add("text-gray-500", "hover:text-gray-700");
                // Remove active underline indicator
                const underline = button.querySelector(".absolute.bottom-0");
                if (underline) {
                    underline.remove();
                }
            }
            // Update aria-selected for accessibility
            button.setAttribute("aria-selected", isActive.toString());
        });
        // Update all tab content panels
        tabContents.forEach((content) => {
            const tabIndex = parseInt(content.getAttribute("data-tab-index") || "0", 10);
            const isActive = tabIndex === targetIndex;
            if (isActive) {
                content.classList.remove("hidden");
                content.setAttribute("aria-hidden", "false");
            }
            else {
                content.classList.add("hidden");
                content.setAttribute("aria-hidden", "true");
            }
        });
        // Update tab titles - show only the title corresponding to the active tab
        tabTitles.forEach((title) => {
            const tabIndex = parseInt(title.getAttribute("data-tab-index") || "0", 10);
            const isActive = tabIndex === targetIndex;
            if (isActive) {
                title.style.display = "";
            }
            else {
                title.style.display = "none";
            }
        });
        // Update URL with tab-specific parameters
        updateUrlForTab(targetIndex);
    };
    /**
     * Handle tab button click
     */
    const handleTabClick = (event) => {
        event.preventDefault();
        const button = event.currentTarget;
        const tabIndex = parseInt(button.getAttribute("data-tab-index") || "0", 10);
        activateTab(tabIndex);
    };
    /**
     * Handle keyboard navigation
     */
    const handleKeyDown = (event) => {
        const button = event.currentTarget;
        const currentIndex = parseInt(button.getAttribute("data-tab-index") || "0", 10);
        let newIndex = currentIndex;
        switch (event.key) {
            case "ArrowLeft":
                event.preventDefault();
                newIndex = currentIndex > 0 ? currentIndex - 1 : tabButtons.length - 1;
                break;
            case "ArrowRight":
                event.preventDefault();
                newIndex = currentIndex < tabButtons.length - 1 ? currentIndex + 1 : 0;
                break;
            case "Home":
                event.preventDefault();
                newIndex = 0;
                break;
            case "End":
                event.preventDefault();
                newIndex = tabButtons.length - 1;
                break;
            default:
                return;
        }
        // Activate the new tab and focus the button
        activateTab(newIndex);
        const targetButton = Array.from(tabButtons).find((btn) => parseInt(btn.getAttribute("data-tab-index") || "0", 10) === newIndex);
        if (targetButton) {
            targetButton.focus();
        }
    };
    // Add click event listeners to all tab buttons
    tabButtons.forEach((button) => {
        button.addEventListener("click", handleTabClick);
        button.addEventListener("keydown", handleKeyDown);
        // Make buttons keyboard accessible
        button.setAttribute("role", "tab");
        button.setAttribute("tabindex", "0");
    });
    // Set ARIA roles for accessibility
    const tabList = element.querySelector(".js-event-tabs");
    if (tabList) {
        tabList.setAttribute("role", "tablist");
    }
    tabContents.forEach((content) => {
        content.setAttribute("role", "tabpanel");
    });
    // Set initial aria-selected state based on current active tab
    tabButtons.forEach((button) => {
        const isActive = button.classList.contains("text-green-700");
        button.setAttribute("aria-selected", isActive.toString());
    });
    /**
     * Handle URL hash navigation
     * If URL contains a hash matching a tab button's id, scroll to that button and activate the tab
     */
    const handleHashNavigation = () => {
        const hash = window.location.hash.substring(1); // Remove the # symbol
        if (!hash) {
            return;
        }
        // Find the tab button with matching id
        const targetButton = Array.from(tabButtons).find((button) => button.id === hash);
        if (targetButton) {
            // Get the tab index from the button
            const tabIndex = parseInt(targetButton.getAttribute("data-tab-index") || "0", 10);
            // Activate the tab
            activateTab(tabIndex);
            // Scroll to the top of the tab section, accounting for padding
            // Get the element's position relative to the document
            const elementRect = element.getBoundingClientRect();
            const currentScrollY = window.scrollY || window.pageYOffset;
            const targetScrollY = currentScrollY + elementRect.top - 12; // Subtract 12px for padding
            // Smooth scroll to the calculated position
            window.scrollTo({
                top: targetScrollY,
                behavior: "smooth",
            });
        }
    };
    // Check hash on initial load
    handleHashNavigation();
    // Listen for hash changes (e.g., when user clicks anchor links)
    window.addEventListener("hashchange", handleHashNavigation);
    // Listen for URL changes (from filters) to save parameters for current tab
    // This uses popstate to detect when filters update the URL
    const handleUrlChange = () => {
        // Save current parameters for the active tab whenever URL changes
        saveTabParams(currentActiveTabIndex);
    };
    // Use MutationObserver to detect URL changes from pushState (used by filters)
    let lastUrl = window.location.href;
    const urlChangeObserver = new MutationObserver(() => {
        const currentUrl = window.location.href;
        if (currentUrl !== lastUrl) {
            lastUrl = currentUrl;
            handleUrlChange();
        }
    });
    // Observe the entire document for URL changes
    urlChangeObserver.observe(document, { subtree: true, childList: true });
    // Also listen to popstate for browser back/forward
    window.addEventListener("popstate", handleUrlChange);
    // Cleanup function
    return () => {
        // Save current state before cleanup
        saveTabParams(currentActiveTabIndex);
        tabButtons.forEach((button) => {
            button.removeEventListener("click", handleTabClick);
            button.removeEventListener("keydown", handleKeyDown);
        });
        window.removeEventListener("hashchange", handleHashNavigation);
        window.removeEventListener("popstate", handleUrlChange);
        urlChangeObserver.disconnect();
    };
};
